import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Trash2, Copy, Check, LogIn, Film } from 'lucide-react';
import { toast } from 'sonner';

interface VideoFile {
  id?: string;
  name: string;
  url: string;
  category: string;
  storage_path: string;
  created_at: string;
}

const CATEGORIES = [
  { value: 'medspa', label: 'Med Spa & Aesthetics' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'corporate', label: 'Corporate & Industrial' },
  { value: 'surgeons_clinics', label: 'Surgeons & Clinics' },
];

const MediaManager = () => {
  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('medspa');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      if (session) fetchVideos();
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      if (session) fetchVideos();
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Logged in!');
    }
    setLoggingIn(false);
  };

  const fetchVideos = async () => {
    const { data, error } = await supabase
      .from('portfolio_videos')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to load videos');
      return;
    }

    setVideos(
      (data || []).map((v: any) => ({
        id: v.id,
        name: v.name,
        url: v.url,
        category: v.category,
        storage_path: v.storage_path,
        created_at: v.created_at,
      }))
    );
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    setUploading(true);
    const fileName = `${Date.now()}-${file.name}`;
    const { error: uploadError } = await supabase.storage.from('videos').upload(fileName, file);

    if (uploadError) {
      toast.error('Upload failed: ' + uploadError.message);
      setUploading(false);
      e.target.value = '';
      return;
    }

    const publicUrl = supabase.storage.from('videos').getPublicUrl(fileName).data.publicUrl;

    const { error: dbError } = await supabase.from('portfolio_videos').insert({
      name: file.name,
      url: publicUrl,
      category: selectedCategory as any,
      storage_path: fileName,
    });

    if (dbError) {
      toast.error('Failed to save video metadata');
    } else {
      toast.success('Video uploaded & categorized!');
      fetchVideos();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (video: VideoFile) => {
    const { error: storageError } = await supabase.storage.from('videos').remove([video.storage_path]);
    if (storageError) {
      toast.error('Failed to delete file');
      return;
    }

    if (video.id) {
      await supabase.from('portfolio_videos').delete().eq('id', video.id);
    }

    toast.success('Video deleted');
    fetchVideos();
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    toast.success('URL copied!');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const getCategoryLabel = (value: string) =>
    CATEGORIES.find((c) => c.value === value)?.label || value;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="mb-10">
            <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
              Media Library
            </span>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-foreground mb-4">
              Video Manager
            </h1>
            <p className="text-muted-foreground text-lg">
              Upload, categorize, and manage your portfolio video content.
            </p>
          </div>

          {!isAuthenticated ? (
            <div className="bg-card border border-border rounded-2xl p-8 max-w-md">
              <div className="flex items-center gap-3 mb-6">
                <LogIn className="w-5 h-5 text-accent" />
                <h2 className="font-display font-semibold text-xl text-foreground">Sign In</h2>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <Button type="submit" className="w-full" disabled={loggingIn}>
                  {loggingIn ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Upload Area */}
              <div className="bg-card border-2 border-dashed border-border rounded-2xl p-10 text-center hover:border-accent/50 transition-colors">
                <Film className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <div className="flex items-center justify-center gap-4 mb-4 flex-wrap">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="video/*" onChange={handleUpload} className="hidden" disabled={uploading} />
                  <Button variant="default" size="lg" disabled={uploading} asChild>
                    <span>
                      <Upload className="w-5 h-5 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Video'}
                    </span>
                  </Button>
                </label>
                <p className="text-muted-foreground text-sm mt-3">MP4, MOV, WebM supported</p>
              </div>

              {/* Video List */}
              {videos.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">
                  No videos uploaded yet. Upload your first video above!
                </p>
              ) : (
                <div className="grid gap-6">
                  {videos.map((video) => (
                    <div key={video.id || video.name} className="bg-card border border-border rounded-2xl overflow-hidden shadow-soft">
                      <video src={video.url} controls className="w-full max-h-[400px] bg-foreground/5" />
                      <div className="p-4 flex items-center justify-between gap-4">
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="text-sm text-foreground truncate font-medium">{video.name}</span>
                          <span className="text-xs text-accent font-semibold uppercase tracking-wider">
                            {getCategoryLabel(video.category)}
                          </span>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => copyUrl(video.url)}>
                            {copiedUrl === video.url ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(video)} className="text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MediaManager;
